/* Anime-Pass — Bundle builder
   ---------------------------------------------------------
   Native replacement for the Easy Bundle / AiCartMax volume widget.

   - Four tier cards in a radio group; selecting one reveals N variant
     rows (N = tier quantity), each a square thumbnail + a `Design` select
     of available variants, pre-filled with a different variant.
   - One add-to-cart button batches all N covers into a single
     POST /cart/add.js, then hands the rendered drawer to the cart via the
     documented `ap:cart:updated` event. It never calls AnimePassCart.
   - Prices are computed client-side from the tier's quantity + discount.
     Every variant is the same price, so a Design change never moves them;
     the maths still runs on init so the cards match the Liquid render.

   Plain IIFE, delegated where practical, lazy-inited on IntersectionObserver,
   re-inited on shopify:section:load. No custom elements. */
(function () {
  'use strict';

  function moneyFormatter(fmt) {
    fmt = fmt || '{{amount}}';
    return function (cents) {
      var amount = (cents / 100).toFixed(2);
      var noDec = Math.round(cents / 100).toString();
      var comma = amount.replace('.', ',');
      return fmt
        .replace('{{amount_with_comma_separator}}', comma)
        .replace('{{amount_no_decimals_with_comma_separator}}', noDec)
        .replace('{{amount_no_decimals}}', noDec)
        .replace('{{amount}}', amount);
    };
  }

  function parseJSON(el, fallback) {
    if (!el) return fallback;
    try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
  }

  function root_url() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return r || '/';
  }

  function initBuilder(root) {
    if (root._apBundleInit) return;
    root._apBundleInit = true;

    var money = moneyFormatter(
      (root.closest('[data-ap-money-format]') || root).getAttribute('data-ap-money-format')
    );
    var unit = parseInt(root.getAttribute('data-unit-price'), 10) || 0;
    // Discount rounding mode — must match the Shopify discount class on this
    // product (see the Liquid comment). "product" = Amount off products (floor
    // per unit, then multiply); "order" = Amount off order (floor once).
    var rounding = root.getAttribute('data-ap-bb-rounding') === 'product' ? 'product' : 'order';
    var allVariants = parseJSON(root.querySelector('[data-ap-bundle-variants]'), []);
    var strings = parseJSON(root.querySelector('[data-ap-bundle-strings]'), {});

    function idList(attr) {
      return (root.getAttribute(attr) || '')
        .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    }
    function findVariant(list, id) {
      id = String(id);
      for (var i = 0; i < list.length; i++) {
        if (String(list[i].id) === id) return list[i];
      }
      return null;
    }

    // "Which designs appear" — an optional subset in the merchant's chosen order,
    // validated against what's actually available; blank/all-invalid => all.
    var poolIds = idList('data-ap-bb-pool');
    var variants = allVariants;
    if (poolIds.length) {
      var picked = poolIds
        .map(function (id) { return findVariant(allVariants, id); })
        .filter(Boolean);
      if (picked.length) variants = picked;
    }
    // "Which design each row defaults to" — one id per row; invalid => auto.
    var defaultIds = idList('data-ap-bb-defaults');

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-ap-bb-tier]'));
    var addBtn = root.querySelector('[data-ap-bb-add]');
    var addLabel = root.querySelector('[data-ap-bb-add-label]');
    var status = root.querySelector('[data-ap-bb-status]');
    if (!cards.length || !addBtn || !variants.length) return;

    // No design pickers: the add uses the tier quantity of the first
    // available variant as ONE line, and no rows are rendered.
    var noPicker = root.hasAttribute('data-ap-bb-nopicker');

    // The rows for the selected tier render INSIDE that tier's own card.
    function rowsOf(card) { return card.querySelector('[data-ap-bb-rows]'); }

    var addLabelDefault = addLabel ? addLabel.textContent : '';

    /* ---- Per-card pricing (client-side, matches the Liquid render) ---- */
    cards.forEach(function (card) {
      var qty = parseInt(card.getAttribute('data-qty'), 10) || 1;
      var pct = parseInt(card.getAttribute('data-pct'), 10) || 0;
      var compare = unit * qty;
      // Integer-cent maths, mirroring the Liquid render so the paint and the JS
      // agree and both equal what Shopify charges.
      var disc = rounding === 'product'
        ? Math.floor(unit * pct / 100) * qty
        : Math.floor(unit * qty * pct / 100);
      var total = compare - disc;
      // Per-unit subtitle derives from the SAME floored total; floor the
      // division to match Liquid's integer divided_by.
      var per = Math.floor(total / qty);

      var totalEl = card.querySelector('[data-ap-bb-total]');
      if (totalEl) totalEl.textContent = money(total);

      var compareEl = card.querySelector('[data-ap-bb-compare]');
      if (compareEl) {
        if (pct > 0) { compareEl.textContent = money(compare); compareEl.hidden = false; }
        else { compareEl.hidden = true; }
      }

      var subEl = card.querySelector('[data-ap-bb-sub]');
      if (subEl && subEl.getAttribute('data-tpl') == null) {
        // remember the original template so re-inits stay stable
        subEl.setAttribute('data-tpl', subEl.textContent);
      }
      if (subEl) {
        subEl.textContent = subEl.getAttribute('data-tpl')
          .replace('[unit_price]', money(per))
          .replace('[product_quantity]', String(qty));
      }
    });

    /* ---- Variant rows ---- */
    function makeRow(index) {
      // Merchant-set default for this row (if valid), else a distinct design.
      var pre = findVariant(variants, defaultIds[index]) || variants[index % variants.length];

      var row = document.createElement('div');
      row.className = 'bundle-builder__row';

      var thumb = document.createElement('span');
      thumb.className = 'bundle-builder__thumb';
      var img = document.createElement('img');
      img.loading = 'lazy';
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      if (pre && pre.image) img.src = pre.image;
      thumb.appendChild(img);

      var field = document.createElement('label');
      field.className = 'bundle-builder__field';

      var labelText = document.createElement('span');
      labelText.className = 'bundle-builder__field-label';
      labelText.textContent = strings.designLabel || 'Design';

      var select = document.createElement('select');
      select.className = 'bundle-builder__select';
      select.setAttribute('data-ap-bb-select', '');
      select.setAttribute(
        'aria-label',
        (strings.selectDesign || 'Design {{ number }}').replace(/\{\{\s*number\s*\}\}/, String(index + 1))
      );

      variants.forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = v.id;
        opt.textContent = v.title;
        select.appendChild(opt);
      });
      if (pre) select.value = pre.id;

      select.addEventListener('change', function () {
        var v = findVariant(variants, select.value);
        img.src = v && v.image ? v.image : '';
      });

      field.appendChild(labelText);
      field.appendChild(select);
      row.appendChild(thumb);
      row.appendChild(field);
      return row;
    }

    function buildRows(card) {
      var wrap = rowsOf(card);
      if (!wrap) return;
      wrap.textContent = '';
      var qty = parseInt(card.getAttribute('data-qty'), 10) || 1;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < qty; i++) frag.appendChild(makeRow(i));
      wrap.appendChild(frag);
    }

    function selectTier(card) {
      cards.forEach(function (c) {
        var on = c === card;
        c.classList.toggle('is-selected', on);
        var radio = c.querySelector('input[type="radio"]');
        if (radio) radio.checked = on;
        // Only the selected card keeps its rows; clear the others.
        if (!on) { var w = rowsOf(c); if (w) w.textContent = ''; }
      });
      buildRows(card);
    }

    function selectedCard() {
      return cards.filter(function (c) { return c.classList.contains('is-selected'); })[0] || cards[0];
    }

    cards.forEach(function (card) {
      var radio = card.querySelector('input[type="radio"]');
      card.addEventListener('click', function (e) {
        if (e.target.closest('select, a, button')) return;
        selectTier(card);
      });
      if (radio) {
        radio.addEventListener('change', function () {
          if (radio.checked) selectTier(card);
        });
      }
    });

    // Default: the tier flagged data-default, else the checked radio, else first.
    var initial =
      cards.filter(function (c) { return c.getAttribute('data-default') === 'true'; })[0] ||
      cards.filter(function (c) { var r = c.querySelector('input[type="radio"]'); return r && r.checked; })[0] ||
      cards[0];
    selectTier(initial);

    /* ---- Add to cart (single batched add) ---- */
    // Errors are shown visibly (red) AND announced; success is announced only.
    function setStatus(msg, isError) {
      if (!status) return;
      status.textContent = msg || '';
      status.classList.toggle('is-error', !!isError);
      status.classList.toggle('visually-hidden', !isError);
    }

    addBtn.addEventListener('click', function () {
      if (addBtn.disabled) return;

      var items;
      if (noPicker) {
        // One line: the first available variant × the selected tier quantity.
        var first = allVariants[0];
        var qty = parseInt(selectedCard().getAttribute('data-qty'), 10) || 1;
        if (!first) return;
        items = [{ id: first.id, quantity: qty }];
      } else {
        var selects = selectedCard().querySelectorAll('[data-ap-bb-select]');
        var counts = {};
        Array.prototype.forEach.call(selects, function (s) {
          var id = parseInt(s.value, 10);
          if (!id) return;
          counts[id] = (counts[id] || 0) + 1;
        });
        items = Object.keys(counts).map(function (id) {
          return { id: parseInt(id, 10), quantity: counts[id] };
        });
      }
      if (!items.length) return;

      setStatus('');
      addBtn.disabled = true;
      addBtn.classList.add('is-adding');
      if (addLabel) addLabel.textContent = strings.adding || addLabelDefault;

      fetch(root_url() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items, sections: 'anime-pass-cart-drawer' })
      })
        .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(function (data) {
          document.dispatchEvent(new CustomEvent('ap:cart:updated', {
            detail: { sections: data.sections || {} }
          }));
          addBtn.classList.remove('is-adding');
          addBtn.disabled = false;
          if (addLabel) addLabel.textContent = strings.added || addLabelDefault;
          setStatus(strings.added || '');
          setTimeout(function () {
            if (addLabel) addLabel.textContent = addLabelDefault;
          }, 1800);
        })
        .catch(function () {
          addBtn.classList.remove('is-adding');
          addBtn.disabled = false;
          if (addLabel) addLabel.textContent = addLabelDefault;
          setStatus(strings.error || '', true);
        });
    });
  }

  /* ---- Lazy init ---- */
  function boot(root) {
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) { io.disconnect(); initBuilder(root); }
      }, { rootMargin: '300px' });
      io.observe(root);
    } else {
      initBuilder(root);
    }
  }

  function init(ctx) {
    (ctx || document).querySelectorAll('[data-ap-bundle]').forEach(boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelectorAll) return;
    e.target.querySelectorAll('[data-ap-bundle]').forEach(function (root) {
      root._apBundleInit = false;
      boot(root);
    });
  });
})();
